const firstText = (...values) => values.find(value =>
  typeof value === 'string' && value.trim().length > 0,
)?.trim();

const keywordText = value => {
  if (Array.isArray(value)) {
    return value.map(item => String(item).trim()).filter(Boolean).join(', ');
  }
  return firstText(value);
};

const safeUrl = (value, baseUrl) => {
  const candidate = firstText(value);
  if (!candidate) {
    return undefined;
  }
  try {
    const url = new URL(candidate, baseUrl);
    return ['http:', 'https:'].includes(url.protocol) ? url.href : undefined;
  } catch {
    return undefined;
  }
};

const currentCanonicalUrl = currentUrl => {
  try {
    const url = new URL(currentUrl);
    url.search = '';
    url.hash = '';
    return url.href;
  } catch {
    return undefined;
  }
};

/** Resolve the optional SEO contract and current info fields into page metadata. */
export function resolveSeoMetadata(info = {}, options = {}, currentUrl = '') {
  const settings = info?.settings || {};
  const seo = info?.seo || settings?.seo || info?.aggregator_settings?.seo || {};
  const brand = firstText(
    seo.site_name,
    seo.siteName,
    info.aggregator_name,
    info.app_name,
    options.brandName,
  );
  const appName = firstText(options.appName, 'Web App');
  const fallbackTitle = [brand, appName].filter(Boolean).join(' | ');
  const fallbackDescription = brand
    ? `${brand} ${appName} web application.`
    : `${appName} web application.`;
  const canonical = safeUrl(
    firstText(
      seo.canonical_url,
      seo.canonicalUrl,
      info.seo_canonical_url,
      info.canonical_url,
      settings.seo_canonical_url,
    ),
    currentUrl,
  ) || currentCanonicalUrl(currentUrl);
  const image = safeUrl(firstText(
    seo.image,
    seo.og_image,
    seo.ogImage,
    info.seo_image,
    info.og_image,
    settings.seo_image,
    info.aggregator_img,
  ), currentUrl);

  return {
    title: firstText(
      seo.title,
      seo.meta_title,
      seo.metaTitle,
      info.seo_title,
      info.meta_title,
      settings.seo_title,
      fallbackTitle,
    ),
    description: firstText(
      seo.description,
      seo.meta_description,
      seo.metaDescription,
      info.seo_description,
      info.meta_description,
      settings.seo_description,
      fallbackDescription,
    ),
    keywords: keywordText(
      seo.keywords || info.seo_keywords || info.meta_keywords || settings.seo_keywords,
    ) || [brand, appName].filter(Boolean).join(', '),
    image,
    canonical,
    robots: firstText(seo.robots, info.seo_robots, settings.seo_robots, 'index, follow'),
    siteName: brand,
    themeColor: firstText(info.theme_color, settings.theme_color),
  };
}

const upsertMeta = (attribute, key, content) => {
  if (!content) {
    return;
  }
  let element = document.head.querySelector(`meta[${attribute}="${key}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
};

const upsertLink = (rel, href) => {
  if (!href) {
    return;
  }
  let element = document.head.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
};

/** Apply info-endpoint metadata. This is a no-op in native runtimes. */
export function applyWebSeo(info, options = {}) {
  if (typeof document === 'undefined' || typeof window === 'undefined' || !document.head) {
    return;
  }
  const metadata = resolveSeoMetadata(info, options, window.location.href);
  if (metadata.title) {
    document.title = metadata.title;
  }
  upsertMeta('name', 'description', metadata.description);
  upsertMeta('name', 'keywords', metadata.keywords);
  upsertMeta('name', 'robots', metadata.robots);
  upsertMeta('name', 'theme-color', metadata.themeColor);
  upsertMeta('property', 'og:type', 'website');
  upsertMeta('property', 'og:title', metadata.title);
  upsertMeta('property', 'og:description', metadata.description);
  upsertMeta('property', 'og:site_name', metadata.siteName);
  upsertMeta('property', 'og:url', metadata.canonical);
  upsertMeta('property', 'og:image', metadata.image);
  upsertMeta('name', 'twitter:card', metadata.image ? 'summary_large_image' : 'summary');
  upsertMeta('name', 'twitter:title', metadata.title);
  upsertMeta('name', 'twitter:description', metadata.description);
  upsertMeta('name', 'twitter:image', metadata.image);
  upsertLink('canonical', metadata.canonical);
  if (metadata.image) {
    upsertLink('icon', metadata.image);
    upsertLink('apple-touch-icon', metadata.image);
  }

  let structuredData = document.head.querySelector('script[data-cc-seo="web-application"]');
  if (!structuredData) {
    structuredData = document.createElement('script');
    structuredData.type = 'application/ld+json';
    structuredData.setAttribute('data-cc-seo', 'web-application');
    document.head.appendChild(structuredData);
  }
  structuredData.textContent = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: metadata.title,
    description: metadata.description,
    url: metadata.canonical,
    image: metadata.image,
    applicationCategory: 'BusinessApplication',
    publisher: metadata.siteName ? {
      '@type': 'Organization',
      name: metadata.siteName,
      logo: metadata.image,
    } : undefined,
  });
}
